/**
 * Word Collection Hook
 *
 * Provides access to word collection store with automatic loading on songId change.
 * Manages lifecycle and cleanup.
 *
 * Scope 06: Rhyme state removed. Collection is flat words only.
 */

import { useEffect, useMemo, useRef } from 'react'
import { useWordCollectionStore } from '../store/wordCollectionStore'

export function useWordCollection(songId: string | null) {
  // Select specific state slices to avoid unnecessary re-renders
  const words = useWordCollectionStore(state => state.words)
  const currentSongId = useWordCollectionStore(state => state.currentSongId)
  const searchQuery = useWordCollectionStore(state => state.searchQuery)
  const usedWordsInLyrics = useWordCollectionStore(state => state.usedWordsInLyrics)
  const isSyncing = useWordCollectionStore(state => state.isSyncing)
  const lastSyncAt = useWordCollectionStore(state => state.lastSyncAt)
  const syncError = useWordCollectionStore(state => state.syncError)
  const isLoading = useWordCollectionStore(state => state.isLoading)
  const loadError = useWordCollectionStore(state => state.loadError)
  const syncTimeoutId = useWordCollectionStore(state => state.syncTimeoutId)

  // Select actions (these are stable references)
  const loadCollection = useWordCollectionStore(state => state.loadCollection)
  const addWord = useWordCollectionStore(state => state.addWord)
  const updateWord = useWordCollectionStore(state => state.updateWord)
  const deleteWord = useWordCollectionStore(state => state.deleteWord)
  const changeWordStatus = useWordCollectionStore(state => state.changeWordStatus)
  const updateWordNotes = useWordCollectionStore(state => state.updateWordNotes)
  const setSearchQuery = useWordCollectionStore(state => state.setSearchQuery)
  const markWordUsed = useWordCollectionStore(state => state.markWordUsed)
  const syncWithLyrics = useWordCollectionStore(state => state.syncWithLyrics)
  const saveCollection = useWordCollectionStore(state => state.saveCollection)
  const getWordById = useWordCollectionStore(state => state.getWordById)
  const getCollectedWords = useWordCollectionStore(state => state.getCollectedWords)
  const getSetAsideWords = useWordCollectionStore(state => state.getSetAsideWords)

  // Track syncTimeoutId in a ref for unmount cleanup (avoids reloading on timeout changes)
  const syncTimeoutRef = useRef(syncTimeoutId)
  syncTimeoutRef.current = syncTimeoutId

  // Load collection when songId changes
  useEffect(() => {
    if (songId) {
      loadCollection(songId)
    }

    // Cleanup on unmount only - use ref to get current timeout ID
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current)
      }
    }
  }, [songId, loadCollection])

  // Compute derived selectors
  const collectedWords = useMemo(() => words.filter(w => w.status === 'collected'), [words])
  const setAsideWords = useMemo(() => words.filter(w => w.status === 'set_aside'), [words])

  // Return state and actions
  return {
    // Core data
    words,
    currentSongId,

    // UI state
    searchQuery,
    usedWordsInLyrics,

    // Sync state
    isSyncing,
    lastSyncAt,
    syncError,

    // Loading state
    isLoading,
    loadError,

    // Actions
    loadCollection,
    addWord,
    updateWord,
    deleteWord,
    changeWordStatus,
    updateWordNotes,
    setSearchQuery,
    markWordUsed,
    syncWithLyrics,
    saveCollection,

    // Derived selectors
    collectedWords,
    setAsideWords,
    getWordById,
    getCollectedWords,
    getSetAsideWords,
  }
}
